import { create } from 'zustand';
import type { OrderListParams } from '@/api/endpoints/orders';
import { toast } from '@/stores/toast.store';
import { runOrdersCsvExport } from '../lib/run-orders-csv-export';

type OrdersExportStatus = 'idle' | 'running' | 'success' | 'error';

const SUCCESS_DISMISS_MS = 4_000;

interface OrdersExportStore {
  status: OrdersExportStatus;
  rowsExported: number;
  estimatedTotal: number | null;
  elapsedMs: number;
  errorMessage: string | null;
  /** True while chunks are in flight — survives route changes within the app shell. */
  isRunning: boolean;
  startExport: (params: OrderListParams, estimatedTotal: number) => void;
  dismiss: () => void;
}

/**
 * Global orders CSV export job. Keeps large exports alive when the user navigates
 * away from the list page and drives the AppShell progress banner (DEF-01 UX).
 */
export const useOrdersExportStore = create<OrdersExportStore>((set, get) => ({
  status: 'idle',
  rowsExported: 0,
  estimatedTotal: null,
  elapsedMs: 0,
  errorMessage: null,
  isRunning: false,

  startExport: (params, estimatedTotal) => {
    if (get().isRunning) {
      toast.info('An orders export is already in progress.');
      return;
    }

    set({
      status: 'running',
      isRunning: true,
      rowsExported: 0,
      estimatedTotal,
      elapsedMs: 0,
      errorMessage: null,
    });

    void (async () => {
      try {
        await runOrdersCsvExport(params, {
          estimatedTotal,
          onProgress: ({ rowsExported, estimatedTotal: progressTotal, elapsedMs }) => {
            set({
              rowsExported,
              estimatedTotal: progressTotal ?? estimatedTotal,
              elapsedMs,
              status: 'running',
              isRunning: true,
            });
          },
        });

        const finalRows = get().rowsExported;
        set({
          status: 'success',
          isRunning: false,
          rowsExported: finalRows,
        });
        toast.success(
          finalRows > 0
            ? `Downloaded ${finalRows.toLocaleString()} orders as CSV.`
            : 'Orders CSV downloaded.',
        );

        window.setTimeout(() => {
          if (get().status === 'success') {
            get().dismiss();
          }
        }, SUCCESS_DISMISS_MS);
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'CSV export failed';
        set({
          status: 'error',
          isRunning: false,
          errorMessage: message,
        });
        toast.error(message);
      }
    })();
  },

  dismiss: () => {
    set({
      status: 'idle',
      isRunning: false,
      rowsExported: 0,
      estimatedTotal: null,
      elapsedMs: 0,
      errorMessage: null,
    });
  },
}));
